import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import FocusTrap from 'focus-trap-react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage, TranslationKey } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { navigationMenuAPI, announcementBarAPI } from '../utils/api';
import { NavigationMenuItem, AnnouncementBar as AnnouncementBarType } from '../types';
import SearchDropdown from './SearchDropdown';
import CartSidebar from './CartSidebar';

const AnnouncementBar = styled.div<{ isVisible: boolean; $bgColor?: string; $textColor?: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  background: ${props => props.$bgColor || '#280212'};
  color: ${props => props.$textColor || '#ffffff'};
  text-align: center;
  padding: 0.5rem 3rem;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-variant-numeric: lining-nums;
  font-family: 'Cormorant Garamond', serif;
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    font-size: 0.5625rem;
    padding: 0.4rem 2rem;
    white-space: nowrap;
  }
`;

const AnnouncementText = styled.span`
  color: rgba(255, 255, 255, 0.95);

  span {
    margin: 0 0.375rem;
    color: #ffffff;
    font-weight: 500;
  }
`;

const AnnouncementCloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  opacity: 1;
  transition: color 0.2s ease;
  font-size: 0.875rem;
  line-height: 1;
  position: absolute;
  right: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #ffffff;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    right: 0.875rem;
  }
`;

const HeaderWrapper = styled.div<{ scrolled: boolean; searchOpen: boolean; isProductPage?: boolean; isHidden?: boolean; announcementVisible: boolean }>`
  position: fixed;
  top: ${props => props.announcementVisible ? '33px' : '0'};
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: ${props => props.searchOpen ? 'none' : '1px solid rgba(0, 0, 0, 0.08)'};
  transform: ${props => props.isHidden ? 'translateY(-100%)' : 'translateY(0)'};
  width: 100vw;
  max-width: 100vw;
  overflow-x: hidden;

  @media (max-width: 768px) {
    top: ${props => props.announcementVisible ? '30px' : '0'};
  }
`;

const HeaderContainer = styled.header`
  padding: 1.5rem 3rem 1rem;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);

  .mobile-menu-button {
    display: none;

    @media (max-width: 768px) {
      display: flex;
    }
  }

  .desktop-menu-button {
    display: flex;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .desktop-search-button {
    display: flex;
    margin-left: 0.25rem;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .mobile-search-button {
    display: none;

    @media (max-width: 768px) {
      display: flex;
    }
  }

  .desktop-user-button {
    display: flex;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .desktop-world-button {
    display: flex;

    @media (max-width: 768px) {
      display: none;
    }
  }

  .mobile-world-button {
    display: none;

    @media (max-width: 768px) {
      display: flex;
    }
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1rem 0.75rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
  }
`;

const HeaderBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled(Link)<{ scrolled: boolean; searchOpen: boolean; isProductPage?: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-decoration: none;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 22px;
    width: auto;
    display: block;
    color: var(--color-charcoal);

    @media (max-width: 768px) {
      height: 16px;
    }
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 3rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ scrolled: boolean; searchOpen: boolean }>`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  color: var(--color-charcoal);
  opacity: 0.85;
  transition: all 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 0.5px;
    background: currentColor;
    transition: width 0.3s ease;
  }

  &:hover {
    opacity: 1;

    &::after {
      width: 100%;
    }
  }
`;

const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const LeftIconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 0rem;
    margin-right: 0rem;
    flex: 1;
    justify-content: flex-start;
  }
`;

const RightIconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 0rem;
    margin-left: 0rem;
    flex: 1;
    justify-content: flex-end;
  }
`;

const IconButton = styled.button<{ scrolled: boolean; searchOpen?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--color-charcoal);
  opacity: 0.8;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.5;
  }

  span {
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-family: 'Cormorant Garamond', serif;
    line-height: 1;
  }

`;

const CartDot = styled.span`
  position: absolute;
  top: 9px;
  right: 4px;
  background: #000000;
  width: 6px;
  height: 6px;
  border-radius: 50%;
`;

const MobileMenuOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  z-index: 9999;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  flex-direction: column;
  width: 100vw;
  max-width: 100vw;
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;

  @media (min-width: 769px) {
    display: none;
  }
`;

const DesktopMenuOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 360px;
  background: #ffffff;
  z-index: 9999;
  padding: 0;
  overflow-y: auto;
  box-shadow: 2px 0 30px rgba(0, 0, 0, 0.04);
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopMenuBackdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9998;
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 4.5rem 1.5rem 2rem;
  flex: 1;
`;

const DesktopNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 7rem 2.5rem 2rem;
  flex: 1;
`;

const MobileNavLink = styled(Link)<{ $isCraft?: boolean }>`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.813rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  color: #1a1a1a;
  transition: opacity 0.2s ease;
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    opacity: 0.6;
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.4;
  }
`;

const DesktopNavLink = styled(Link)<{ $hasArrow?: boolean; $isCraft?: boolean }>`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  color: #000000;
  transition: opacity 0.2s ease;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.7;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    opacity: 0.6;
  }

  svg {
    width: 12px;
    height: 12px;
    opacity: 0.6;
  }
`;

const DesktopNavButton = styled.button`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.813rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  color: #1a1a1a;
  transition: opacity 0.2s ease;
  padding: 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.4;
  }
`;

const MobileNavButton = styled.button`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.813rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  color: #1a1a1a;
  transition: opacity 0.2s ease;
  padding: 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.4;
  }
`;

const MobileMenuFooter = styled.div`
  padding: 1.5rem 1.5rem 2rem;
  margin-top: auto;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopLanguageSelector = styled.button`
  position: absolute;
  top: 2.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.688rem;
  font-weight: 400;
  color: #999;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    color: #666;
  }

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
  }

  @media (min-width: 769px) {
    left: auto;
    right: 2.5rem;
  }
`;


const MobileLoginLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.813rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-decoration: none;
  color: #1a1a1a;
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const MobileLogoutButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #1a1a1a;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2.5rem;
  left: 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  opacity: 1;
  color: #000000;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.6;
  }

  svg {
    width: 22px;
    height: 22px;
    stroke: currentColor;
    stroke-width: 1;
  }

  @media (min-width: 769px) {
    left: 2.5rem;
  }
`;

// Side Drawer Components
const DrawerOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: ${props => props.$isOpen ? '1' : '0'};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 10000;
`;

const SideDrawer = styled.div<{ $isOpen: boolean; $isLoggedIn?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  height: 100dvh; /* Use dynamic viewport height */
  background: #ffffff;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 10001;
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
  }
`;

const DrawerHeader = styled.div<{ $isLoggedIn?: boolean }>`
  padding: 30px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DrawerTitle = styled.h2<{ $isLoggedIn?: boolean }>`
  font-family: 'Cormorant Garamond', serif;
  font-size: 24px;
  font-weight: 300;
  color: #2c2c2c;
  margin: 0;
`;

const CloseDrawerButton = styled.button<{ $isLoggedIn?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  color: #2c2c2c;
  font-size: 24px;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.6;
  }
`;

const DrawerContent = styled.div`
  padding: 40px 30px;
`;

// Login Form Styles
const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-family: 'Cormorant Garamond', serif;
  font-size: 12px;
  font-weight: 400;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FormInput = styled.input`
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  background: #ffffff;
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  color: #2c2c2c;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2c2c2c;
    background: #ffffff;
  }

  &::placeholder {
    color: #999;
  }
`;

const LoginButton = styled.button`
  padding: 16px;
  background: #2c2c2c;
  color: #ffffff;
  border: none;
  font-family: 'Cormorant Garamond', serif;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;

  &:hover {
    background: #1a1a1a;
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid #e0e0e0;

  p {
    font-family: 'Cormorant Garamond', serif;
    font-size: 14px;
    color: #666;
    margin-bottom: 12px;
  }

  a {
    color: #2c2c2c;
    text-decoration: underline;
    font-weight: 500;

    &:hover {
      opacity: 0.7;
    }
  }
`;

// Logged-in state styles
const UserInfo = styled.div`
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 2rem;
`;

const UserGreeting = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.75rem;
  font-weight: 400;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 0.75rem;
`;

const UserName = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 300;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const UserEmail = styled.p`
  font-size: 0.813rem;
  color: #999;
  font-weight: 300;
`;

const AccountNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 2rem;
`;

const AccountLink = styled(Link)<{ $isActive?: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  font-weight: ${props => props.$isActive ? '500' : '400'};
  letter-spacing: 0.02em;
  color: ${props => props.$isActive ? '#1a1a1a' : '#2c2c2c'};
  text-decoration: none;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: color 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: #1a1a1a;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 1rem 0;
  margin-top: 2rem;
  background: transparent;
  color: #999;
  border: none;
  font-size: 0.813rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.3s ease;
  text-align: left;

  &:hover {
    color: #1a1a1a;
  }
`;

// Language Selector Styles
const LanguageOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  padding: 20px;
  border: 1px solid ${props => props.$isActive ? '#2c2c2c' : '#e0e0e0'};
  background: ${props => props.$isActive ? '#f8f8f8' : '#ffffff'};
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 16px;

  &:hover {
    border-color: #2c2c2c;
  }
`;

const LanguageFlag = styled.span`
  font-size: 24px;
`;

const LanguageName = styled.span<{ $isActive?: boolean }>`
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  color: #2c2c2c;
  font-weight: ${(props: any) => props.$isActive ? '500' : '400'};
`;

const HeaderLuxury: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [loginDrawerOpen, setLoginDrawerOpen] = useState(false);
  const [languageDrawerOpen, setLanguageDrawerOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const { state: authState, logout, login } = useAuth();
  const [announcementVisible, setAnnouncementVisible] = useState(false);
  const [announcementData, setAnnouncementData] = useState<AnnouncementBarType | null>(null);
  const { state: cartState, showCartDrawer, hideCartDrawer, isCartDrawerOpen, showMobileCartDrawer } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [navItems, setNavItems] = useState<NavigationMenuItem[]>([]);

  // Fetch navigation items
  useEffect(() => {
    const fetchNavigation = async () => {
      try {
        const data = await navigationMenuAPI.getMenu();
        if (data?.mainMenu) {
          setNavItems(data.mainMenu.filter(item => item.isActive).sort((a, b) => a.order - b.order));
        }
      } catch (error) {
        console.error('Error fetching navigation:', error);
        // Fallback to default items if API fails
        setNavItems([
          { label: 'Valevenio Coffee Selection', labelEn: 'Valevenio Coffee Selection', link: '/coffee-selection', order: 0, isActive: true },
          { label: 'Watch Cases', labelEn: 'Watch Cases', link: '/watch-cases', order: 1, isActive: true },
          { label: 'The Craft Behind', labelEn: 'The Craft Behind', link: '/craft-behind', order: 2, isActive: true, highlight: true },
          { label: 'Bize Ulaşın', labelEn: 'Contact Us', link: '/contact', order: 3, isActive: true }
        ]);
      }
    };
    fetchNavigation();
  }, []);

  const handleAnnouncementClose = () => {
    setAnnouncementVisible(false);
    sessionStorage.setItem('announcementDismissed', 'true');
    // Add class to body for CSS-only adjustments
    document.body.classList.add('announcement-closed');
  };

  // Fetch announcement data on mount
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const data = await announcementBarAPI.get();
        setAnnouncementData(data);
      } catch (error) {
        console.error('Error fetching announcement:', error);
        // Use default if API fails - bordo color matching footer
        setAnnouncementData({
          text: 'Tüm siparişlerde ücretsiz kargo',
          textEn: 'Free shipping on all orders',
          isActive: true,
          visibility: 'guests',
          backgroundColor: '#280212',
          textColor: '#ffffff'
        });
      }
    };
    fetchAnnouncement();
  }, []);

  // Update announcement visibility based on API data and auth state
  useEffect(() => {
    if (!announcementData) return;

    const dismissed = sessionStorage.getItem('announcementDismissed');
    if (dismissed === 'true') {
      setAnnouncementVisible(false);
      document.body.classList.add('announcement-closed');
      return;
    }

    if (!announcementData.isActive) {
      setAnnouncementVisible(false);
      document.body.classList.add('announcement-closed');
      return;
    }

    // Check visibility setting
    let shouldShow = false;
    switch (announcementData.visibility) {
      case 'guests':
        shouldShow = !authState.isAuthenticated;
        break;
      case 'loggedIn':
        shouldShow = authState.isAuthenticated;
        break;
      case 'everyone':
        shouldShow = true;
        break;
      default:
        shouldShow = !authState.isAuthenticated;
    }

    setAnnouncementVisible(shouldShow);

    if (shouldShow) {
      document.body.classList.remove('announcement-closed');
    } else {
      document.body.classList.add('announcement-closed');
    }
  }, [announcementData, authState.isAuthenticated]);

  const handleLogout = () => {
    logout();
    showNotification(language === 'TR' ? 'Çıkış yapıldı' : 'Logged out successfully');
  };

  // No CSS variable updates - prevents flickering!

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);

      // Keep header always visible
      setIsHeaderHidden(false);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close all overlays when navigating to a new page
    setMobileMenuOpen(false);
    setDesktopMenuOpen(false);
    setSearchOpen(false);
    // Don't close cart drawer on location change - let it stay open

    // Reset zoom on mobile when navigating
    if (window.innerWidth <= 768) {
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      }
      // Force scroll to top and reset any zoom
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);



  useEffect(() => {
    // Use a more reliable scroll locking method
    const shouldLockScroll = mobileMenuOpen || desktopMenuOpen || searchOpen || loginDrawerOpen || languageDrawerOpen;
    const isMobileView = window.innerWidth <= 768;

    if (shouldLockScroll) {
      const scrollY = window.scrollY;

      if (isMobileView) {
        // Mobile: Use full scroll lock (position: fixed prevents background scrolling completely)
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';

        return () => {
          const scrollPosition = parseInt(document.body.style.top || '0') * -1;

          // Restore scroll
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';

          // Restore scroll position immediately without animation
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant' as ScrollBehavior
          });
        };
      } else {
        // Desktop: Use scroll event prevention instead of overflow hidden
        // This preserves position: sticky behavior
        const preventScroll = (e: WheelEvent) => {
          e.preventDefault();
        };

        const preventKeyScroll = (e: KeyboardEvent) => {
          const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
          if (scrollKeys.includes(e.key)) {
            e.preventDefault();
          }
        };

        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('keydown', preventKeyScroll);

        return () => {
          window.removeEventListener('wheel', preventScroll);
          window.removeEventListener('keydown', preventKeyScroll);
        };
      }
    }
  }, [mobileMenuOpen, desktopMenuOpen, searchOpen, loginDrawerOpen, languageDrawerOpen]);

  // Escape key handler for drawers/overlays
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (loginDrawerOpen) setLoginDrawerOpen(false);
        if (languageDrawerOpen) setLanguageDrawerOpen(false);
        if (mobileMenuOpen) setMobileMenuOpen(false);
        if (desktopMenuOpen) setDesktopMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [loginDrawerOpen, languageDrawerOpen, mobileMenuOpen, desktopMenuOpen]);

  const isHomePage = location.pathname === '/';
  const isProductPage = location.pathname.startsWith('/product/');
  const headerScrolled = scrolled; // Use normal scroll behavior for all pages

  return (
    <>
      <AnnouncementBar
        id="announcement-bar"
        isVisible={announcementVisible}
        $bgColor={announcementData?.backgroundColor}
        $textColor={announcementData?.textColor}
      >
        <AnnouncementText>
          {language === 'TR'
            ? (announcementData?.text || 'Tüm siparişlerde ücretsiz kargo')
            : (announcementData?.textEn || announcementData?.text || 'Free shipping on all orders')
          }
        </AnnouncementText>
        <AnnouncementCloseButton onClick={handleAnnouncementClose} aria-label="Close announcement">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </AnnouncementCloseButton>
      </AnnouncementBar>
      <HeaderWrapper id="main-header" scrolled={headerScrolled} searchOpen={searchOpen} isProductPage={isProductPage} isHidden={isHeaderHidden} announcementVisible={announcementVisible}>
        <HeaderContainer>
          <HeaderTop>
            {/* Left side - Mobile: Hamburger | Desktop: Menu and Search */}
            <LeftIconGroup>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={() => setMobileMenuOpen(true)}
                className="mobile-menu-button"
                aria-label="Open menu"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <line x1="2" y1="6" x2="22" y2="6" strokeWidth="1" />
                  <line x1="2" y1="12" x2="22" y2="12" strokeWidth="1" />
                  <line x1="2" y1="18" x2="22" y2="18" strokeWidth="1" />
                </svg>
              </IconButton>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={() => setDesktopMenuOpen(true)}
                className="desktop-menu-button"
                aria-label="Open menu"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <line x1="2" y1="6" x2="22" y2="6" strokeWidth="1" />
                  <line x1="2" y1="12" x2="22" y2="12" strokeWidth="1" />
                  <line x1="2" y1="18" x2="22" y2="18" strokeWidth="1" />
                </svg>
                <span>Menu</span>
              </IconButton>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={isProductPage ? undefined : () => setSearchOpen(!searchOpen)}
                className="desktop-search-button"
                aria-label="Search"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="7" strokeWidth="1" />
                  <path d="m21 21-4.35-4.35" strokeWidth="1" />
                </svg>
                <span>{t('header.search')}</span>
              </IconButton>
            </LeftIconGroup>

            {/* Center - Logo */}
            <Logo to="/" scrolled={headerScrolled} searchOpen={searchOpen} isProductPage={isProductPage}>
              <img src="/valevenio_logo.svg" alt="VALEVENIO" />
            </Logo>

            {/* Right side - Mobile: Search and Bag | Desktop: User, Bag, World */}
            <RightIconGroup>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={() => {
                  if (authState.isAuthenticated) {
                    setLoginDrawerOpen(true);
                  } else {
                    navigate('/login');
                  }
                }}
                className="desktop-user-button"
                aria-label="Account"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <circle cx="12" cy="8" r="5" />
                  <path d="M3 21v-2a7 7 0 0 1 7-7h4a7 7 0 0 1 7 7v2" />
                </svg>
              </IconButton>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={() => setSearchOpen(!searchOpen)}
                className="mobile-search-button"
                aria-label="Search"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </IconButton>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={() => {
                  const isMobile = window.innerWidth <= 768;
                  if (isMobile) {
                    // Show mobile cart drawer on mobile
                    showMobileCartDrawer();
                  } else {
                    // Show desktop cart sidebar on desktop
                    showCartDrawer();
                  }
                }}
                aria-label="Shopping bag"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M9 8V6C9 4.34315 10.3431 3 12 3C13.6569 3 15 4.34315 15 6V8" strokeLinecap="round"/>
                  <path d="M3 11C3 9.89543 3.89543 9 5 9H19C20.1046 9 21 9.89543 21 11V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V11Z"/>
                </svg>
                {cartState.itemCount > 0 && (
                  <CartDot />
                )}
              </IconButton>
              <IconButton
                scrolled={headerScrolled}
                searchOpen={searchOpen}
                onClick={() => {
                  const newLang = language === 'TR' ? 'EN' : 'TR';
                  setLanguage(newLang);
                }}
                className="desktop-world-button"
                style={{ minWidth: '45px', justifyContent: 'center' }}
              >
                <span style={{ fontSize: '0.75rem', fontWeight: 400, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {language === 'TR' ? 'TUR' : 'ENG'}
                </span>
              </IconButton>
            </RightIconGroup>
          </HeaderTop>

        </HeaderContainer>
      </HeaderWrapper>
      <SearchDropdown isOpen={searchOpen} onClose={() => setSearchOpen(false)} scrolled={headerScrolled} />

      <DesktopMenuBackdrop isOpen={desktopMenuOpen} onClick={() => setDesktopMenuOpen(false)} />

      <DesktopMenuOverlay isOpen={desktopMenuOpen} role="dialog" aria-modal="true" aria-label="Navigation menu">
        <CloseButton onClick={() => setDesktopMenuOpen(false)} aria-label="Close menu">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </CloseButton>

        <DesktopNav>
          {navItems.map((item) => (
            <DesktopNavLink
              key={item._id || item.link}
              to={item.link}
              onClick={() => setDesktopMenuOpen(false)}
              $hasArrow={item.link === '/coffee-selection'}
              $isCraft={item.link === '/craft-behind'}
              target={item.openInNewTab ? '_blank' : undefined}
              rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
            >
              <span>
                {item.link === '/craft-behind'
                  ? 'The Craft Behind'
                  : (language === 'EN' && item.labelEn ? item.labelEn : item.label)
                }
              </span>
              {item.link === '/coffee-selection' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              )}
            </DesktopNavLink>
          ))}
        </DesktopNav>
      </DesktopMenuOverlay>

      <MobileMenuOverlay isOpen={mobileMenuOpen} role="dialog" aria-modal="true" aria-label="Navigation menu">
        <CloseButton onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </CloseButton>

        <TopLanguageSelector
          onClick={() => {
            const newLang = language === 'TR' ? 'EN' : 'TR';
            setLanguage(newLang);
          }}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
            <circle cx="8" cy="8" r="6.5" />
            <line x1="1.5" y1="8" x2="14.5" y2="8" />
            <ellipse cx="8" cy="8" rx="3" ry="6.5" />
          </svg>
          {language === 'TR' ? 'TUR' : 'ENG'}
        </TopLanguageSelector>

        <MobileNav>
          {navItems.map((item) => (
            <MobileNavLink
              key={item._id || item.link}
              to={item.link}
              onClick={() => setMobileMenuOpen(false)}
              $isCraft={item.link === '/craft-behind'}
              target={item.openInNewTab ? '_blank' : undefined}
              rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
            >
              <span>
                {item.link === '/craft-behind'
                  ? 'The Craft Behind'
                  : (language === 'EN' && item.labelEn ? item.labelEn : item.label)
                }
              </span>
              {item.link === '/coffee-selection' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              )}
            </MobileNavLink>
          ))}
        </MobileNav>

        <MobileMenuFooter>
          {authState.isAuthenticated ? (
            <>
              <MobileLoginLink to="/profile" onClick={() => setMobileMenuOpen(false)}>
                <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24">
                  <path d="M16.125 8.75c-.184 2.478-2.063 4.5-4.125 4.5s-3.944-2.021-4.125-4.5c-.187-2.578 1.64-4.5 4.125-4.5 2.484 0 4.313 1.969 4.125 4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M3.017 20.747C3.783 16.5 7.922 14.25 12 14.25s8.217 2.25 8.984 6.497" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10"></path>
                </svg>
                {t('header.profile')}
              </MobileLoginLink>
              <MobileLogoutButton
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                title={language === 'TR' ? 'Çıkış Yap' : 'Log Out'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16,17 21,12 16,7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </MobileLogoutButton>
            </>
          ) : (
            <MobileLoginLink to="/login" onClick={() => setMobileMenuOpen(false)}>
              <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24">
                <path d="M16.125 8.75c-.184 2.478-2.063 4.5-4.125 4.5s-3.944-2.021-4.125-4.5c-.187-2.578 1.64-4.5 4.125-4.5 2.484 0 4.313 1.969 4.125 4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M3.017 20.747C3.783 16.5 7.922 14.25 12 14.25s8.217 2.25 8.984 6.497" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10"></path>
              </svg>
              {t('header.login')}
            </MobileLoginLink>
          )}
        </MobileMenuFooter>
      </MobileMenuOverlay>

      <CartSidebar
        isOpen={isCartDrawerOpen}
        onClose={hideCartDrawer}
      />

      {/* Login Drawer */}
      <DrawerOverlay $isOpen={loginDrawerOpen} onClick={() => setLoginDrawerOpen(false)} />
      <FocusTrap active={loginDrawerOpen} focusTrapOptions={{ allowOutsideClick: true, escapeDeactivates: false }}>
        <SideDrawer $isOpen={loginDrawerOpen} $isLoggedIn={authState.isAuthenticated} role="dialog" aria-modal="true" aria-label={authState.isAuthenticated ? (language === 'TR' ? 'Hesabım' : 'My Account') : 'Login'}>
          <DrawerHeader $isLoggedIn={authState.isAuthenticated}>
            <DrawerTitle $isLoggedIn={authState.isAuthenticated}>
              {authState.isAuthenticated ? (language === 'TR' ? 'Hesabım' : 'My Account') : t('login.title')}
            </DrawerTitle>
            <CloseDrawerButton $isLoggedIn={authState.isAuthenticated} onClick={() => setLoginDrawerOpen(false)} aria-label="Close account panel">×</CloseDrawerButton>
          </DrawerHeader>
        <DrawerContent>
          {authState.isAuthenticated ? (
            <>
              <UserInfo>
                <UserGreeting>{language === 'TR' ? 'Hoş Geldiniz' : 'Welcome'}</UserGreeting>
                <UserName>
                  {(authState.user as any)?.firstName || (language === 'TR' ? 'Kullanıcı' : 'User')} {(authState.user as any)?.lastName || ''}
                </UserName>
                <UserEmail>{authState.user?.email}</UserEmail>
              </UserInfo>

              <AccountNav>
                <AccountLink
                  to="/profile"
                  onClick={() => setLoginDrawerOpen(false)}
                  $isActive={location.pathname === '/profile'}
                >
                  <span>{language === 'TR' ? 'Profil Bilgileri' : 'Profile Information'}</span>
                </AccountLink>
                <AccountLink
                  to="/orders"
                  onClick={() => setLoginDrawerOpen(false)}
                  $isActive={location.pathname === '/orders'}
                >
                  <span>{language === 'TR' ? 'Siparişlerim' : 'My Orders'}</span>
                </AccountLink>
              </AccountNav>

              <LogoutButton onClick={() => { handleLogout(); setLoginDrawerOpen(false); }}>
                {language === 'TR' ? 'Oturumu Kapat' : 'Log Out'}
              </LogoutButton>
            </>
          ) : (
            <>
              <LoginForm onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await login(loginForm.email, loginForm.password);
                  setLoginDrawerOpen(false);
                  setLoginForm({ email: '', password: '' });
                  showNotification(language === 'TR' ? 'Başarıyla giriş yapıldı' : 'Successfully logged in');
                } catch (error) {
                  console.error('Login failed:', error);
                  showNotification(language === 'TR' ? 'Giriş başarısız' : 'Login failed', 'error');
                }
              }}>
                <FormGroup>
                  <FormLabel>{t('login.email')}</FormLabel>
                  <FormInput
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>{t('login.password')}</FormLabel>
                  <FormInput
                    type="password"
                    placeholder={t('login.passwordPlaceholder')}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                  />
                </FormGroup>
                <LoginButton type="submit">{t('login.submit')}</LoginButton>
              </LoginForm>
              <RegisterLink>
                <p>{t('login.noAccount')}</p>
                <Link to="/register" onClick={() => setLoginDrawerOpen(false)}>
                  {t('login.register')}
                </Link>
              </RegisterLink>
            </>
          )}
        </DrawerContent>
      </SideDrawer>
      </FocusTrap>

      {/* Language Drawer */}
      <DrawerOverlay $isOpen={languageDrawerOpen} onClick={() => setLanguageDrawerOpen(false)} />
      <FocusTrap active={languageDrawerOpen} focusTrapOptions={{ allowOutsideClick: true, escapeDeactivates: false }}>
        <SideDrawer $isOpen={languageDrawerOpen} role="dialog" aria-modal="true" aria-label="Language selection">
          <DrawerHeader>
            <DrawerTitle>{t('language.title')}</DrawerTitle>
            <CloseDrawerButton onClick={() => setLanguageDrawerOpen(false)} aria-label="Close language panel">×</CloseDrawerButton>
          </DrawerHeader>
        <DrawerContent>
          <LanguageOptions>
            <LanguageOption
              $isActive={language === 'TR'}
              onClick={() => { setLanguage('TR'); setLanguageDrawerOpen(false); }}
            >
              <LanguageFlag>🇹🇷</LanguageFlag>
              <LanguageName $isActive={language === 'TR'}>{t('language.turkish')}</LanguageName>
            </LanguageOption>
            <LanguageOption
              $isActive={language === 'EN'}
              onClick={() => { setLanguage('EN'); setLanguageDrawerOpen(false); }}
            >
              <LanguageFlag>🇬🇧</LanguageFlag>
              <LanguageName $isActive={language === 'EN'}>{t('language.english')}</LanguageName>
            </LanguageOption>
          </LanguageOptions>
        </DrawerContent>
      </SideDrawer>
      </FocusTrap>
    </>
  );
};

export default HeaderLuxury;
